import React, { useState } from 'react';
import { Card, Table, Row, Col, Button } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useOwnerCash } from '@/hooks/useOwnerIntelligence';
import { useCurrency } from '@/contexts/CurrencyContext';
import {
  PrimaryKpiCard,
  OiPageFrame,
  OiEmptyState,
  ProportionalBar,
  OiDetailDrawer,
  OiCashChasePanel,
  OiMetricHelp,
} from '@/components/owner-intelligence/OwnerIntelligenceComponents';
import { OiRadialGauge } from '@/components/owner-intelligence/charts/OiCharts';
import { OI_CHART } from '@/components/owner-intelligence/charts/OiChartTheme';

const AGING_COLORS = ['var(--oi-emerald)', '#fcd34d', 'var(--oi-amber)', '#f87171', 'var(--oi-crimson)'];

const CashIntelligencePage: React.FC = () => {
  const { data, isLoading, error } = useOwnerCash();
  const { formatCurrency } = useCurrency();
  const navigate = useNavigate();
  const [drawerRow, setDrawerRow] = useState<Record<string, unknown> | null>(null);

  const forecast = data?.cash_forecast_hint;
  const collectionRate = forecast?.collection_rate_pct ?? 0;

  const arSegments = (data?.ar_aging ?? []).map((b, i) => ({
    label: b.bucket,
    amount: b.amount,
    color: AGING_COLORS[i % AGING_COLORS.length],
  }));

  return (
    <OiPageFrame
      subtitle="Cash position, receivables, payables, and working capital."
      loading={isLoading && !data}
      error={Boolean(error)}
      errorMessage="Cash intelligence unavailable"
    >
      {data ? (
        <>
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col xs={12} sm={6}>
              <PrimaryKpiCard label="Receivables" value={formatCurrency(Number(data.ar_total ?? 0))} />
            </Col>
            <Col xs={12} sm={6}>
              <PrimaryKpiCard label="Payables" value={formatCurrency(Number(data.ap_total ?? 0))} />
            </Col>
            <Col xs={12} sm={6}>
              <PrimaryKpiCard label="Inventory value" value={formatCurrency(Number(data.inventory_value ?? 0))} />
            </Col>
            <Col xs={12} sm={6}>
              <PrimaryKpiCard
                label="Working capital est."
                value={formatCurrency(Number(data.working_capital_estimate ?? 0))}
              />
              <OiMetricHelp title="Working capital estimate">
                Receivables plus inventory value minus payables. A simplified liquidity snapshot — not a audited balance sheet.
              </OiMetricHelp>
            </Col>
          </Row>

          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col xs={24} md={12}>
              <Card title="Collection rate (MTD)" bordered={false}>
                <OiRadialGauge score={Math.min(100, Math.round(collectionRate))} label="Collected vs invoiced" />
                <div style={{ textAlign: 'center', fontSize: 12, color: 'var(--oi-text-muted)' }}>
                  {formatCurrency(Number(forecast?.collections_mtd ?? 0))} / {formatCurrency(Number(forecast?.invoiced_mtd ?? 0))}
                </div>
              </Card>
            </Col>
            <Col xs={24} md={12}>
              <Card title="Unbilled delivered work" bordered={false} style={{ borderColor: OI_CHART.target }}>
                <div className="oi-mono-value" style={{ fontSize: 32, fontWeight: 700 }}>
                  {formatCurrency(Number(data.unbilled_work?.estimatedNetValue ?? 0))}
                </div>
                <div style={{ color: 'var(--oi-text-muted)', marginBottom: 12 }}>
                  {data.unbilled_work?.linesWithAvailability ?? 0} delivery lines pending invoice
                </div>
                <Button type="primary" size="small" onClick={() => navigate('/growth/invoices')}>
                  Invoice now
                </Button>
              </Card>
            </Col>
          </Row>

          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col xs={24} lg={12}>
              <Card title="AR aging" bordered={false}>
                <ProportionalBar segments={arSegments} />
                <Table
                  size="small"
                  pagination={false}
                  rowKey="bucket"
                  style={{ marginTop: 16 }}
                  dataSource={data.ar_aging ?? []}
                  columns={[
                    { title: 'Bucket', dataIndex: 'bucket' },
                    { title: 'Amount', dataIndex: 'amount', render: (v: number) => formatCurrency(v) },
                    { title: 'Count', dataIndex: 'count' },
                  ]}
                />
              </Card>
            </Col>
            <Col xs={24} lg={12}>
              <Card title="AP aging" bordered={false}>
                <ProportionalBar
                  segments={(data.ap_aging ?? []).map((b, i) => ({
                    label: b.bucket,
                    amount: b.amount,
                    color: AGING_COLORS[i % AGING_COLORS.length],
                  }))}
                />
                <Table
                  size="small"
                  pagination={false}
                  rowKey="bucket"
                  style={{ marginTop: 16 }}
                  dataSource={data.ap_aging ?? []}
                  columns={[
                    { title: 'Bucket', dataIndex: 'bucket' },
                    { title: 'Amount', dataIndex: 'amount', render: (v: number) => formatCurrency(v) },
                    { title: 'Count', dataIndex: 'count' },
                  ]}
                />
              </Card>
            </Col>
          </Row>

          <Card title="Customers to chase first" style={{ marginBottom: 16 }} bordered={false}>
            {(data.customers_to_chase ?? []).length === 0 ? (
              <OiEmptyState
                variant="positive"
                title="No overdue customers"
                body="All receivables are current."
              />
            ) : (
              <Table
                size="small"
                rowKey="customer_id"
                dataSource={data.customers_to_chase ?? []}
                onRow={(row) => ({
                  onClick: () => setDrawerRow(row as unknown as Record<string, unknown>),
                  style: { cursor: 'pointer' },
                })}
                columns={[
                  { title: 'Customer', dataIndex: 'customer_name' },
                  { title: 'Outstanding', dataIndex: 'total_outstanding', render: (v: number) => formatCurrency(v) },
                  { title: 'Overdue', dataIndex: 'total_overdue', render: (v: number) => formatCurrency(v) },
                ]}
              />
            )}
          </Card>

          <Card title="Money stuck summary" bordered={false}>
            <ProportionalBar
              segments={(data.money_stuck ?? []).map((m, i) => ({
                label: m.category,
                amount: m.amount,
                color: AGING_COLORS[i % AGING_COLORS.length],
              }))}
            />
          </Card>

          <OiDetailDrawer
            open={Boolean(drawerRow)}
            onClose={() => setDrawerRow(null)}
            title="Customer chase detail"
            actionLabel="View statement"
            onAction={() => navigate('/fin/reports/customer-outstanding')}
          >
            {drawerRow ? <OiCashChasePanel row={drawerRow} /> : null}
          </OiDetailDrawer>
        </>
      ) : null}
    </OiPageFrame>
  );
};

export default CashIntelligencePage;
